import * as Sentry from '@sentry/react';
import { ApolloClient } from 'apollo-client';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { onError } from 'apollo-link-error';
import { ApolloLink } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import {
  InMemoryCache,
  defaultDataIdFromObject as importedDefaultDataIdFromObject,
} from 'apollo-cache-inmemory';
import { isPlainObject } from 'lodash';

export const defaultDataIdFromObject = importedDefaultDataIdFromObject;

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path, extensions }) => {
      if (message === 'DownForMaintenance') {
        window.__handleDownForMaintenance?.();
      }

      console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`);
      if (message !== 'DownForMaintenance') {
        Sentry.captureException(new Error(message), {
          tags: { graphql: 'true' },
          extra: { path, extensions },
        });
      }
      const stacktrace =
        extensions && extensions.exception && extensions.exception.stacktrace;
      if (stacktrace) {
        console.error(stacktrace.join('\n'));
      }
    });
  }

  if (networkError) {
    console.error(`[Network Error]: ${networkError}`);
    Sentry.captureException(networkError, {
      tags: { errorType: 'network' },
    });
  }
});

/**
 * Wrap a mutation's `update` option so a failure inside it is reported.
 *
 * apollo-client 2.6 only ever calls `update` through apollo-utilities'
 * `tryFunctionOrLogError`, which is a bare `try { f() } catch (e) {
 * console.error(e) }`, on the optimistic pass and on the real result alike. A
 * throw therefore leaves the mutation resolving as a success while the cache
 * is half-updated, and the only trace is a console.error in the browser: the
 * error link above never sees it, because it never reaches the link.
 */
export function reportingCacheUpdate(name, update) {
  const report = (error) =>
    Sentry.captureException(error, {
      tags: { errorType: 'cacheUpdate' },
      extra: { update: name },
    });

  return (...args) => {
    let result;

    try {
      result = update(...args);
    } catch (error) {
      // Rethrown so Apollo behaves exactly as it does today -- it logs the
      // throw and carries on, and a caller that chains further work off this
      // update still skips it. This adds the report and nothing else, and
      // leaves Apollo to do the logging.
      report(error);
      throw error;
    }

    if (!result || typeof result.then !== 'function') {
      return result;
    }

    // An `update` that starts async work hands its promise back -- Apollo
    // ignores an `update`'s return value -- so a rejection is reported here
    // rather than surfacing as a contextless unhandled rejection. Nothing
    // else logs this one, unlike the throw above.
    //
    // This can double up with the error link: a resetStore() rejection is an
    // ApolloError wrapping the same network or GraphQL failure the link has
    // already reported. Kept deliberately, because the two events say
    // different things -- the link's says a request failed, this one says a
    // cache update was left unfinished, which is the part that explains what
    // the user is looking at -- and `errorType: 'cacheUpdate'` is what tells
    // them apart in Sentry.
    return result.catch((error) => {
      console.error(error);
      report(error);
    });
  };
}

export function createClientLink(uri, authTokenCallback) {
  const httpLink = new BatchHttpLink({ uri });
  const authLink = setContext(async (_, { headers }) => {
    const token = await authTokenCallback();
    if (token) {
      headers = {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
    }

    const versionString = `${window.ReactAppEnv.client.version} (${window.ReactAppEnv.client.build})`;
    headers = {
      ...headers,
      'X-Client-Version': versionString,
      'X-Client-Path': window.location.pathname,
    };

    return { headers };
  });
  return ApolloLink.from([errorLink, authLink, httpLink]);
}

export function createAuthenticatedApolloClient(options) {
  options = { ...options };
  options.link =
    options.link || createClientLink(options.uri, options.authTokenCallback);
  options.cache =
    options.cache && !isPlainObject(options.cache)
      ? options.cache
      : new InMemoryCache(options.cache);
  return new ApolloClient(options);
}
